import Accordions from "@/common/components/Accordions";
import Box from "@/common/components/Box";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { formatDateTime } from "@/common/utils/formatters";
import { maintenanceForwardingsDirectiveEntryKlasses } from "@/features/maintenance/features/workflows/constants/forwardings/maintenanceForwardingsDirectiveEntryKlasses.constant";
import { useMaintenanceWorkflowForwardings } from "@/features/maintenance/features/workflows/hooks/useMaintenanceWorkflowForwardings";
import { MaintenanceForwardingStatus } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwarding.type";
import { MaintenanceForwardingsDirectiveEntryKlass } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwardingsDirectiveEntryKlasses.type";
import { MaintenaceWorkflowData } from "@/features/maintenance/features/workflows/types/maintenanceWorkflows.type";
import { useMemo } from "react";
import MaintenanceWorkflowForwardingShow from "./MaintenanceWorkflowForwardingShow";
import MaintenanceWorkflowForwardingShowLoading from "./MaintenanceWorkflowForwardingShowLoading";

type MaintenanceWorkflowForwardingListItem = {
  id: string;
  status: MaintenanceForwardingStatus;
  directive_entry_type: MaintenanceForwardingsDirectiveEntryKlass;
  created_at: string;
};

interface MaintenanceWorkflowForwardingsTabProps {
  workflow?: MaintenaceWorkflowData;
  directiveEntryKlass: MaintenanceForwardingsDirectiveEntryKlass;
}

const previousForwardingsTitle: Partial<Record<MaintenanceForwardingsDirectiveEntryKlass, string>> = {
  [MaintenanceForwardingsDirectiveEntryKlass.DiagnosticEntry]: 'Diagnósticos e orçamentos anteriores',
  [MaintenanceForwardingsDirectiveEntryKlass.ExecutionEntry]: 'Execuções anteriores',
};

const MaintenanceWorkflowForwardingsTab = ({
  workflow,
  directiveEntryKlass,
}: MaintenanceWorkflowForwardingsTabProps) => {
  const {
    data: forwardings,
    isPending: forwardingsIsPending,
    error: forwardingsError = {} as any,
    refetch: refetchForwardings,
    dataUpdatedAt: forwardingsUpdatedAt,
  } = useMaintenanceWorkflowForwardings<MaintenanceWorkflowForwardingListItem[]>({
    workflowId: workflow?.id,
    filters: {
      directiveEntryType: directiveEntryKlass,
    },
  });

  const forwarding = useMemo(() => forwardings?.[0], [forwardingsUpdatedAt]);

  const previousForwardings = useMemo(() => (forwardings || []).slice(1), [forwardingsUpdatedAt]);

  if (forwardingsIsPending) {
    return <MaintenanceWorkflowForwardingShowLoading />;
  }

  return (
    <ErrorMessageWrapper className="flex-1" statusCode={forwardingsError?.status} refetchFunction={refetchForwardings}>
      <div className="flex-1 overflow-auto flex flex-col gap-4">
        {forwarding && (
          <MaintenanceWorkflowForwardingShow
            key={forwarding.id}
            forwardingId={forwarding.id}
            directiveEntryKlass={directiveEntryKlass}
          />
        )}

        {previousForwardings.length > 0 && (
          <Box className="flex flex-col gap-4 p-4 bg-blue-100/10">
            <p className="font-bold text-lg">
              {previousForwardingsTitle[directiveEntryKlass] || 'Encaminhamentos anteriores'}
            </p>

            <Accordions
              className="p-0"
              triggerClassName="p-4"
              items={previousForwardings.map((previousForwarding, index) => ({
                index,
                id: previousForwarding.id,
                head: (
                  <div className="flex items-center gap-0.5 text-xs italic">
                    <span>
                      {maintenanceForwardingsDirectiveEntryKlasses[directiveEntryKlass]?.status?.[previousForwarding.status]?.label}
                    </span>

                    {previousForwarding.created_at && (
                      <>
                        <span>•</span>
                        <span className="text-white-400">{formatDateTime(new Date(previousForwarding.created_at))}</span>
                      </>
                    )}
                  </div>
                ),
                body: (
                  <MaintenanceWorkflowForwardingShow
                    key={previousForwarding.id}
                    forwardingId={previousForwarding.id}
                    directiveEntryKlass={directiveEntryKlass}
                    noActions
                  />
                ),
              }))}
            />
          </Box>
        )}
      </div>
    </ErrorMessageWrapper>
  );
};

export default MaintenanceWorkflowForwardingsTab;
